import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { hospitalAdminApi } from "@/api/hospitalAdmin";

function StatCard({ label, value, hint }: { label: string; value: string | number; hint?: string }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-semibold">{value}</div>
        {hint ? <p className="text-xs text-muted-foreground mt-1">{hint}</p> : null}
      </CardContent>
    </Card>
  );
}

export default function MetaDashboardPage() {
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["meta-dashboard"],
    queryFn: () => hospitalAdminApi.getMetaDashboard(),
    refetchInterval: 60_000,
  });

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Meta Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Today&apos;s Conversions API activity and dataset quality (IST).
          </p>
        </div>
        <button
          type="button"
          className="text-sm text-primary underline-offset-4 hover:underline"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          {isFetching ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard label="Leads sent today" value={data?.leadsSentToday ?? 0} />
            <StatCard label="Registrations sent today" value={data?.registrationsSentToday ?? 0} />
            <StatCard label="Appointments sent today" value={data?.appointmentsSentToday ?? 0} />
            <StatCard label="Revenue events sent today" value={data?.revenueEventsSentToday ?? 0} />
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              label="Success rate"
              value={`${data?.successRate ?? 0}%`}
              hint="Sent / attempted today"
            />
            <StatCard label="Failed events" value={data?.failedEvents ?? 0} hint="Failed or dead today" />
            <StatCard
              label="Dataset quality score"
              value={data?.datasetQualityScore != null ? data.datasetQualityScore : "—"}
            />
            <StatCard
              label="Event match quality"
              value={data?.eventMatchQuality != null ? data.eventMatchQuality : "—"}
            />
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Revenue sent to Meta</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-semibold">
                ₹{(data?.revenueSentToMeta ?? 0).toLocaleString("en-IN")}
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Sum of revenue amounts on Purchase events sent successfully today.
              </p>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
